import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Formik } from "formik";
import { graphql, navigate, type PageProps, useStaticQuery } from "gatsby";
import { useState } from "react";
import { object, string } from "yup";

import { FetchApiError, type ValidateLocalAccountResponse } from "@repo/api";
import { cs } from "@repo/ui";
import { useCookieConsents } from "../../context/CookieContext";
import useIsDesktop from "../../desktop/useIsDesktop";
import { useValidateLocalAccount } from "../../hooks-mutations/useValidateLocalAccount";
import bulletPointImg from "../../images/bulletXS.png";
import Attention from "../AttentionBlock";
import CookieDrawer from "../Drawer/CookieDrawer";
import CookieButton from "../Drawer/CookieDrawer/CookieButton";
import Form from "../Form/Form";
import TextInput from "../Form/Input/Text";
import FormSubmit from "../Form/Submit";
import { InternalButtonLink } from "../Link";
import LoginButton from "../LoginButton";
import { isLoggedInMsal } from "../LoginButton/useLogin";
import {
	loginErrorToText,
	loginRedirect,
} from "../LoginButton/useLogin.helpers";
import { useLoginError } from "../LoginButton/useLoginError";
import Overview from "../Overview";
import { BodyTextM, HeadingL, HeadingXS } from "../Typography";

interface imageProps {
	intro_image: {
		publicURL: string;
	};
	aegon: {
		publicURL: string;
	};
	ing: {
		publicURL: string;
	};
}

const introImage = (): imageProps => {
	return useStaticQuery(
		graphql`
			query RozcestnikImages {
				intro_image: file(relativePath: { eq: "rozcestnik.png" }) {
					publicURL
					childImageSharp {
						gatsbyImageData
					}
				}
				aegon: file(relativePath: { eq: "aegon.jpg" }) {
					childImageSharp {
						gatsbyImageData
					}
				}
			}
		`
	);
};



const GuidepostSimplified = ({
	pageProps,
}: {
	pageProps: PageProps;
}): JSX.Element => {
	const { displayCookieForm, setDisplayCookieForm } = useCookieConsents();
	const msal = useMsal();
	const { mutateAsync: validateLocalAccount, isError } =
		useValidateLocalAccount();
	const { authority, loginError } = useLoginError();
	const loading = msal.inProgress !== InteractionStatus.None;
	const [isEmailFilled, setIsEmailFilled] = useState(false);
	const [filledEmail, setFilledEmail] = useState("");
	const customLoginError = pageProps.location.state?.customLoginError;
	const isDesktop = useIsDesktop();

	return (
		<>
			<Overview
				image={introImage().intro_image.publicURL}
				alt="Přihlášení"
				sx={{
					overflow: { md: "visible", xxs: "hidden" },
					p: { md: 0, xs: 1 },
					pt: { md: 1 },
					borderRadius: 1,
					".MuiCardContent-root": {
						boxShadow: (theme) => theme.customShadows.close,
						borderRadius: 1,
						backgroundColor: "white.main",
					},
					".MuiCardMedia-root": {
						display: { md: "none" },
						backgroundPositionY: "2px",
					},
				}}
			>
				<Box
					sx={{
						ml: { md: 3, xs: 2 },
						mr: { md: 3, xs: 2 }
					}}
				>
					<HeadingL>{cs.crossroad.head}</HeadingL>
					{customLoginError && (
						<Attention severity="error">{customLoginError}</Attention>
					)}

					{authority === undefined && loginError && (
						<Attention
							severity="error"
							title={loginErrorToText[loginError.errorCode] ?? undefined}
						>
							{loginError.errorMessage}
						</Attention>
					)}

					<BodyTextM>
						{cs.crossroad.headText}
					</BodyTextM>

					{process.env.GATSBY_FEATURE_IS_BANKID_DIRECT_ENABLED === "true" ? (
						<LoginButton
							journey="crossroad-bank-id-direct"
							sx={{ width: "100%", mt: 4, px: { xs: 1 }, flexDirection: "row" }}
						/>
					) : (
						<LoginButton journey="bank-id" sx={{ width: "100%", mt: 4 }} />
					)}

					{authority === "B2C_1A_BANKIDPOLICY" && loginError && (
						<Attention
							severity="error"
							title={loginErrorToText[loginError.errorCode] ?? undefined}
						>
							{loginError.errorMessage}
						</Attention>
					)}
					<Divider sx={{ my: 4 }}>
						<Typography variant="bodyTextM" sx={{ color: "grey.dark", px: 3 }}>
							{cs.crossroad.dividerText}
						</Typography>
					</Divider>
					<HeadingXS marginBottom={3}>E-mail</HeadingXS>
					{isError ? (
						<Stack
							mt={5}
							direction="row"
							justifyContent="space-between"
							spacing={5}
						>
							<InternalButtonLink
								to="/prihlaseni/"
								variant="outlined"
								sx={{ width: "50%", py: "11px" }}
							>
								<BodyTextM>Přihlášení</BodyTextM>
							</InternalButtonLink>
							{process.env.GATSBY_FEATURE_IS_REGISTRATION_ENABLED === "true" && (
								<Button
									underline="none"
									disableElevation={true}
									onClick={() => {
										navigate("/nova-registrace/", {
											state: { prefilledUsername: filledEmail },
										});
									}}
									variant="outlined"
									sx={{ width: "50%", py: "11px" }}
								>
									<BodyTextM>Registrace</BodyTextM>
								</Button>

							)}
						</Stack>
					) : (
						<Formik
							initialValues={{ email: "" }}
							validationSchema={object().shape({
								email: string().email().required(),
							})}
							onSubmit={async ({ email }, helpers) => {
								const isLoggedIn = await isLoggedInMsal(msal);
								if (isLoggedIn) {
									navigate("/smlouvy");
									return;
								}

								setFilledEmail(email);

								try {
									const res = await validateLocalAccount(email);

									if (res.isAccountRegistered) {
										await loginRedirect(msal, "local-login", email);
									} else {
										await loginRedirect(msal, "local-registration", email);
									}
								} catch (error) {
									if (error instanceof FetchApiError) {
										const err =
											error as FetchApiError<ValidateLocalAccountResponse>;

										if (err.errorResponse.statusCode !== 200) {
											helpers.setErrors({
												email:
													err.errorResponse.userMessage ??
													"Nepodařilo se ověřit e-mail.",
											});
											return;
										}
									}

									helpers.setErrors({
										email: "Nepodařilo se ověřit e-mail.",
									});
								}
							}}
						>
							<Form spacing={2} showSubmitButton={false}>
								<TextInput
									sx={{ mb: 4 }}
									updateFormikOnChange
									placeholder={cs.crossroad.emailPlaceholder}
									label=""
									name="email"
									onChangeCaptureEvent={(evt) => {
										setIsEmailFilled(Boolean(evt.target.value));
									}}
									inputProps={{
										inputMode: "email",
									}}

								/>
								<FormSubmit
									variant="contained"
									ButtonComponent={LoadingButton}
									loading={loading}
									text={{
										submitting: "...",
										label: cs.crossroad.emailButton,
									}}
									sx={{
										width: "100%",
										textAlign: "center",
										px: { xs: 1 },
									}}
								/>

								{authority &&
									["B2C_1A_LOGINPOLICY", "B2C_1_PrototypeLogin"].includes(
										authority
									) &&
									loginError && (
										<Attention
											severity="error"
											title={loginErrorToText[loginError.errorCode] ?? undefined}
										>
											{loginError.errorMessage}
										</Attention>
									)}
							</Form>
						</Formik>
					)}
				</Box>
			</Overview>
			<Stack
				style={{ marginTop: 12 }}
				spacing={5}
				sx={{
					p: 5,
					boxShadow: (theme) => theme.customShadows.close,
					borderRadius: 1,
					backgroundColor: "white.main",
				}}
			>
				<HeadingL>{cs.crossroad.productHead}</HeadingL>
				<BodyTextM>
					<Stack style={{ marginBottom: 12 }}>
						{cs.crossroad.productText}
					</Stack>
					<Stack marginLeft={-4} fontSize={isDesktop ? 16 : 14} flexWrap={"wrap"}>
						<Stack flexDirection={"row"} flexWrap="wrap" justifyContent={"space-between"}>
							<Stack width={{ xxs: "100%", xs: "53%" }}>
								<ul data-clear style={{ marginTop: 0, listStyleImage: `url(${bulletPointImg})`, textWrap: "nowrap" }}>
									<li style={{ marginBottom: 4 }}>{cs.crossroad.productRisk}</li>
									<li style={{ marginBottom: 4 }}>{cs.crossroad.productInvest}</li>
									<li style={{ marginBottom: 4 }}>{cs.crossroad.productBenefit}</li>
								</ul>
							</Stack>
							<Stack width={{ xxs: "100%", xs: "47%" }} >
								<ul data-clear style={{ marginTop: 0, listStyleImage: `url(${bulletPointImg})`, textWrap: "nowrap" }}>
									<li style={{ marginBottom: 4 }}>{cs.crossroad.productBonus}</li>
									<li style={{ marginBottom: 4 }}>{cs.crossroad.productCare}</li>
								</ul>
							</Stack>
						</Stack>
						<Stack>
							<ul data-clear style={{ marginTop: 0, listStyleImage: `url(${bulletPointImg})` }}>
								<li>{cs.crossroad.productAegon}</li>
							</ul>
						</Stack>
					</Stack>
				</BodyTextM>

				<InternalButtonLink
					to="https://mojenn20.nn.cz"
					sx={{ width: "100%", textAlign: "center", px: { xxs: 5 } }}
					variant="outlined"
					disabled={loading || isEmailFilled}
				>
					{cs.crossroad.productButton}
				</InternalButtonLink>
			</Stack>

			<Stack>
				<CookieButton onClick={() => { setDisplayCookieForm(true); }} />
				{displayCookieForm && <CookieDrawer floating />}
			</Stack>
		</>
	);
}

export default GuidepostSimplified;
